import { useTranslation } from "react-i18next";
import type { AnalysisHealthCategoryDelta, AppAnalysisHealth } from "@repo/shared";

import { useLocale } from "@/hooks/use-locale";
import { deltaToneClass, formatSignedCount, isCategoryScored } from "@/lib/health-format";

import { KpiCell, KpiStrip } from "./kpi";

interface CompareCategoriesGridProps {
  /** All five categories, in the canonical order, as the API returns them. */
  deltas: AnalysisHealthCategoryDelta[];
  /** Health payloads of both ends — a category is comparable only if both scored it. */
  fromHealth: AppAnalysisHealth | null;
  toHealth: AppAnalysisHealth | null;
}

/** Per-category score movement, mirroring the detail screen's categories strip. */
export function CompareCategoriesGrid({
  deltas,
  fromHealth,
  toHealth,
}: CompareCategoriesGridProps) {
  const { t } = useTranslation("analyses");
  const { language } = useLocale();

  return (
    <section className="flex flex-col gap-2.5">
      <h2 className="text-[11px] font-bold tracking-[0.6px] text-faint uppercase">
        {t("categories.title")}
      </h2>
      <KpiStrip>
        {deltas.map((category) => {
          const label = t(`category.${category.id}`);

          // A category one of the two payloads did not score keeps its slot, dimmed.
          if (
            !isCategoryScored(fromHealth, category.id) ||
            !isCategoryScored(toHealth, category.id)
          ) {
            return (
              <KpiCell
                key={category.id}
                label={label}
                value="—"
                valueClassName="text-dim"
                subMeta={t("categories.notScored")}
                className="opacity-60"
              />
            );
          }

          // A category either analysis did not score has no comparable delta.
          if (category.delta === null || category.from === null || category.to === null) {
            return (
              <KpiCell
                key={category.id}
                label={label}
                value="—"
                valueClassName="text-dim"
                subMeta={t("compare.categories.notScored")}
              />
            );
          }

          return (
            <KpiCell
              key={category.id}
              label={label}
              value={formatSignedCount(category.delta, language)}
              valueClassName={deltaToneClass(category.delta, "higher-is-better")}
              subMeta={t("compare.kpi.range", { from: category.from, to: category.to })}
            />
          );
        })}
      </KpiStrip>
    </section>
  );
}
